import { Injectable } from '@angular/core'
import {
  NgModalServiceInterface,
  ModalResponse,
} from '../../models/modal.model'
import { NgNotificationServiceInterface } from '../../models/notification.model'
import {
  ExecutionModalCode,
  ExecutionNotificationCode,
  parseExecutionModal,
  parseExecutionNotification,
} from './libs/execution-notification.lib'
import { Execution } from './models/execution.entity'
import {
  ExecutionField,
  ExecutionSearchParams,
} from './models/execution-search.model'
import {
  ExecutionsListingData,
  ExecutionsListingPage,
} from './models/execution-listing.model'
import {
  combineLatest,
  from,
  map,
  Observable,
  of,
  switchMap,
  concatMap,
  scan,
  last,
} from 'rxjs'
import { ExecutionsService } from './executions.service'
import { Page } from '../../models/util.model'
import { ActivitiesService, Activity } from '../activities'
import { parseExecutionPageKeys } from './libs/executions-page.lib'
import { uniqBy } from 'lodash'

@Injectable({
  providedIn: 'root',
})
export class ExecutionsRepository {
  constructor(
    private executionsService: ExecutionsService,
    private activitiesService: ActivitiesService,
    private ngModalService?: NgModalServiceInterface,
    private ngNotificationService?: NgNotificationServiceInterface,
  ) {}

  /**
   * Search goods-returns and external data
   * @param searchParams - the search params
   * @param fields - the fields
   * @param listingData - the already loaded listing data
   * @returns the observable for search goods-returns
   */
  searchExecutions$(
    searchParams: ExecutionSearchParams,
    fields: ExecutionField[],
    listingData?: ExecutionsListingData,
    returnAll?: boolean,
  ): Observable<ExecutionsListingPage> {
    const needsPagination =
      searchParams.limit !== undefined &&
      searchParams.limit > this.executionsService.limit

    return needsPagination
      ? this._paginateExecutions$(searchParams, fields, listingData)
      : this._searchExecutions$(searchParams, fields, listingData, returnAll)
  }

  /**
   * Search goods returns with relative external data
   * @param searchParams - the search params
   * @param fields - the goods-returns fields
   * @param listingData - the already loaded listing data
   * @returns the observable<ExecutionsListingPage> for search goods-returns
   */
  private _searchExecutions$(
    searchParams: ExecutionSearchParams,
    fields: ExecutionField[],
    listingData?: ExecutionsListingData,
    returnAll?: boolean,
  ): Observable<ExecutionsListingPage> {
    return this.executionsService
      .search$(searchParams, returnAll)
      .pipe(
        switchMap((page) =>
          this._loadExtData$(page, fields, listingData).pipe(
            map((extData) => ({ ...page, extData })),
          ),
        ),
      )
  }

  /**
   * Load external page goods-returns data
   * @param page - the goods-returns page
   * @param fields - the goods-returns fields
   * @returns the observable for load external data
   */
  private _loadExtData$(
    page: Page<Execution>,
    fields: ExecutionField[],
    listingData?: ExecutionsListingData,
  ): Observable<ExecutionsListingData> {
    const extDataKeys = parseExecutionPageKeys(page)
    const obs$: Partial<{
      activities: Observable<Activity[]>
    }> = {}

    if (fields.includes('activity._id') && extDataKeys.activityIds) {
      const existingActivities = listingData?.activities || []
      const hasAllActivities = extDataKeys.activityIds.every((id) =>
        existingActivities.some((channel) => channel._id === id),
      )

      if (!hasAllActivities) {
        obs$.activities = this.activitiesService.list$({
          _id: extDataKeys.activityIds,
        })
      } else {
        obs$.activities = of(existingActivities)
      }
    }

    if (!Object.keys(obs$).length) {
      return of({} as ExecutionsListingData)
    }

    return combineLatest(obs$).pipe(
      map((results) => {
        const extData: ExecutionsListingData = {}
        if (results.activities) extData.activities = results.activities
        return extData
      }),
    )
  }

  /**
   * Search more than 200 goods-returns with relative external data
   * @param searchParams - the search params
   * @param fields - the goods-returns fields
   * @param listingData - the already loaded listing data
   * @returns the observable<ExecutionsListingPage> for search goods-returns
   */
  private _paginateExecutions$(
    searchParams: ExecutionSearchParams,
    fields: ExecutionField[],
    listingData?: ExecutionsListingData,
  ): Observable<ExecutionsListingPage> {
    const totalCount = searchParams.limit || this.executionsService.limit
    const pagesCount = Math.ceil(totalCount / this.executionsService.limit)

    return from(
      Array.from({ length: pagesCount }, (_, i) => ({
        ...searchParams,
        limit: this.executionsService.limit,
        offset: i * this.executionsService.limit,
      })),
    ).pipe(
      concatMap((pageParams) =>
        this._searchExecutions$(pageParams, fields, listingData),
      ),
      scan<ExecutionsListingPage, ExecutionsListingPage>(
        (acc, current) => ({
          totalCount: uniqBy(
            [...(acc.data || []), ...(current.data || [])],
            '_id',
          ).length,
          data: uniqBy([...(acc.data || []), ...(current.data || [])], '_id'),
          extData: { ...acc.extData, ...current.extData },
        }),
        { totalCount: 0, data: [], extData: {} } as ExecutionsListingPage,
      ),
      last(),
    )
  }

  notify(options: {
    code?: ExecutionNotificationCode
    message?: string
  }): void {
    this.ngNotificationService?.show(
      options.message
        ? { title: 'Error', message: options.message, color: 'error' }
        : parseExecutionNotification(options.code),
    )
  }

  async alert(
    code: ExecutionModalCode,
    execution?: Execution,
  ): Promise<ModalResponse> {
    const settings = parseExecutionModal(code, execution)
    if (!this.ngModalService || !settings) {
      return 'closed'
    }
    return await this.ngModalService.showDialog(settings)
  }
}
